import clsx from "clsx"
import { ChangeEvent, createRef, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Panel } from "@/components/Panel"
import { selectOrdersByDriverId } from "@/store/orders"
import { setFocusedOrderArea } from "@/store/ui"
import { Driver, Order } from "@/types"
import { OrderUtils, useSearchBox } from "@/utils"
import { OrderItem } from "@/views/MapView/partials/Orders/OrderItem"

import cls from "../../DriverPanel.module.scss"

export const OrdersSection = ({ driver }: { driver: Driver }) => {
  const orders = useAppSelector(selectOrdersByDriverId(driver.id), shallowEqual)
  const focusedOrderId = useAppSelector((state) => state.ui.focusedOrderId)
  const focusedOrderArea = useAppSelector((state) => state.ui.focusedOrderArea, shallowEqual)
  const isAboutPanelVisible = useAppSelector((state) => Boolean(state.ui.aboutPanel))

  const { isSearchBoxShown, toggleSearchBox, searchValue, setSearchValue } = useSearchBox()
  const ordersFilteredAndByNewest = useMemo(
    () => [...orders].filter((order) => OrderUtils.filterBySearch(order, searchValue)).reverse(),
    [orders, searchValue]
  )
  const ordersInProgress = useMemo(() => orders.filter((order) => OrderUtils.isAssigned(order)), [orders])

  const dispatch = useDispatch()

  const { t } = useTranslation()

  const orderRefs = orders.reduce((acc, order) => {
    acc[order.id] = createRef()
    return acc
  }, {} as Record<Order["id"], React.RefObject<HTMLDivElement>>)

  useEffect(() => {
    if (!focusedOrderArea && !isAboutPanelVisible) {
      dispatch(
        setFocusedOrderArea(
          ordersInProgress.map((order) => ({
            id: order.id,
            pickup: order.pickup && {
              lat: order.pickup.lat,
              lng: order.pickup.lng,
            },
            delivery: order.delivery && {
              lat: order.delivery.lat,
              lng: order.delivery.lng,
            },
          }))
        )
      )
    }
    // eslint-disable-next-line
  }, [dispatch, focusedOrderArea])

  useEffect(() => {
    if (focusedOrderId && orderRefs[focusedOrderId]?.current) {
      orderRefs[focusedOrderId].current?.scrollIntoView({ behavior: "smooth" })
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Card.Section className={cls.orders}>
      <div className={cls.ordersContent}>
        <div className={clsx(cls.header, "h200")}>
          {isSearchBoxShown ? (
            <Panel.SearchInput
              value={searchValue}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
              placeholder={t("search_in_orders")}
            />
          ) : (
            <span>
              {ordersFilteredAndByNewest.length > 0
                ? t("orders:orders", {
                    count: ordersFilteredAndByNewest.length,
                  })
                : t("orders:no_orders")}
              {ordersInProgress.length > 0 && (
                <span className="text-muted ml-1">({t("orders:orders_left", { count: ordersInProgress.length })})</span>
              )}
            </span>
          )}

          <Panel.SearchButton
            isActive={isSearchBoxShown}
            title={isSearchBoxShown ? "Zamknij wyszukiwanie" : t("search_order")}
            onClick={toggleSearchBox}
          />
        </div>

        <div className={cls.list}>
          {ordersFilteredAndByNewest.map((order) => (
            <div key={order.id} ref={orderRefs[order.id]}>
              <OrderItem orderId={order.id} header={false} />
            </div>
          ))}
        </div>
      </div>
    </Card.Section>
  )
}
